<template>
  <v-menu
    v-model="menuVisible"
    offset-y
    @keydown.esc="menuVisible = false"
  >
    <template v-slot:activator="{ on }">
      <div>
        <v-btn
          rounded
          text
          outlined
          class="text-none"
          v-on="on"
        >
          <v-icon
            x-small
            class="mr-2"
          >
            far fa-pen
          </v-icon>
          {{ $t('button.edit_columns') }}
        </v-btn>
      </div>
    </template>

    <v-card :width="hasAggregations || useLongText ? 380 : 250">
      <v-text-field
        v-model="search"
        dense
        outlined
        hide-details
        :label="$t('text_input.search.placeholder')"
        class="pa-2"
        clearable
        @click.stop
      />
      <div style="max-height: 300px; overflow-y: auto;">
        <draggable
          :list="selectedVariables"
          handle=".handle"
          :group="{ name: 'variables' }"
          :invert-swap="true"
          :swap-treshold="0.6"
          :animation="100"
          @update="updateOrder"
          @change="updateOrder"
        >
          <div
            v-for="(variable, index) in selectedVariables"
            :key="variable.value + (variable.aggregation || '') + '_' + index"
          >
            <v-list-item
              @click.stop="unselectVariable(variable)"
            >
              <v-list-item-action>
                <v-checkbox
                  :input-value="true"
                  @click.stop="unselectVariable(variable)"
                />
              </v-list-item-action>
              <v-list-item-content class="pa-0">
                <v-list-item-title class="text-truncate">
                  {{ getVariableDisplayText(variable) }}
                  <span
                    v-if="variable.aggregation"
                    class="text-caption grey--text"
                  >({{ getAggregationText(variable) }})</span>
                </v-list-item-title>
              </v-list-item-content>
              <v-list-item-action>
                <v-icon
                  small
                  class="handle"
                  style="cursor: move"
                >
                  far fa-grip-lines
                </v-icon>
              </v-list-item-action>
            </v-list-item>
          </div>
        </draggable>
      </div>
      <v-divider v-if="filteredUnselectedVariables.length" />
      <v-virtual-scroll
        v-if="filteredUnselectedVariables.length"
        :items="filteredUnselectedVariables"
        :item-height="40"
        :height="Math.min(filteredUnselectedVariables.length * 40, 250)"
        bench="5"
      >
        <template v-slot:default="{ item }">
          <v-list-item
            :key="item.value + (item.aggregation || '') + '_' + item.category"
            @click.stop="selectVariable(item)"
          >
            <v-list-item-action>
              <v-checkbox
                :value="false"
                @click.stop="selectVariable(item)"
              />
            </v-list-item-action>
            <v-list-item-content class="pa-0">
              <v-list-item-title class="text-truncate">
                {{ getVariableDisplayText(item) }}
                <span
                  v-if="item.aggregation"
                  class="text-caption grey--text"
                >({{ getAggregationText(item) }})</span>
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>
      </v-virtual-scroll>
    </v-card>
  </v-menu>
</template>

<script>
import draggable from 'vuedraggable'
import { orderBy } from 'lodash'

export default {
  name: 'VariableSelector',
  components: { draggable },
  props: {
    variables: {
      type: Array,
      required: true
    },
    layoutVariables: {
      type: Array,
      required: true
    },
    showAggregation: {
      type: Boolean,
      default: false
    },
    excludedVariables: {
      type: Array,
      default: () => []
    },
    useLongText: {
      type: Boolean,
      default: false
    },
    customAggregations: {
      type: Array,
      default: null
    }
  },
  data: () => ({
    selectedVariables: [],
    unselectedVariables: [],
    menuVisible: false,
    search: '',
    initialized: false
  }),
  computed: {
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    hasAggregations() {
      return this.showAggregation
    },
    filteredUnselectedVariables() {
      if (!this.search) return this.unselectedVariables

      const searchLower = this.search.toLowerCase()
      return this.unselectedVariables.filter(v => {
        const displayText = this.useLongText && v.long_text ? v.long_text : v.text
        return displayText.toLowerCase().includes(searchLower)
      })
    },
    aggregations() {
      if (this.customAggregations) return this.customAggregations

      return [
        { value: 'DL', text: this.$t('variables.DL.long') },
        { value: 'WL', text: this.$t('variables.WL.long') },
        { value: 'WC', text: this.$t('variables.WC.long') },
        { value: 'AL', text: this.$t('variables.AL.long') },
        { value: 'CL', text: this.$t('variables.CL.long') },
        { value: 'ACWR', text: this.$t('variables.ACWR.long') },
        { value: 'FRES', text: this.$t('variables.FRES.long') },
        { value: 'MON', text: this.$t('variables.MON.long') },
        { value: 'STR', text: this.$t('variables.STR.long') }
      ]
    }
  },
  watch: {
    selectedVariables: {
      deep: true,
      handler: function (newValue) {
        this.$emit('update:layoutVariables', newValue)
      }
    },
    variables: {
      immediate: true,
      handler: function (newVariables) {
        if (newVariables && newVariables.length > 0 && !this.initialized) {
          this.initializeVariables()
          this.initialized = true
        }
      }
    },
    menuVisible() {
      if (this.menuVisible && !this.initialized) {
        this.initializeVariables()
      }
    }
  },
  methods: {
    getVariableDisplayText(variable) {
      return this.useLongText && variable.long_text ? variable.long_text : this.$t(`variables.${variable.value}`)
    },
    getAggregationText(variable) {
      if (!variable.aggregation) return ''

      const agg = this.aggregations.find(a => a.value === variable.aggregation)
      if (agg) return agg.text

      // For suffix_agg-based aggregations, use customAggregations or fallback to key
      if (variable.aggregation === 'group_id') return this.$t('insights.by_group')
      return variable.aggregation.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    },
    initializeVariables() {
      let parent = this.$parent
      let isAdvancedPerformanceView = false

      while (parent) {
        if (parent.$options.name === 'PerformanceAdvancedView') {
          isAdvancedPerformanceView = true
          break
        }
        parent = parent.$parent
      }

      const isPain = (v) => {
        const valuePain = v.value && v.value.toLowerCase().includes('pain')
        const categoryPain = v.category && v.category.toLowerCase().includes('pain')
        return valuePain || categoryPain
      }

      const cleanVariables = this.variables.filter(v => {
        return !(isAdvancedPerformanceView && isPain(v))
      })
      const filteredVariables = cleanVariables.filter(v => !this.excludedVariables.includes(v.value))

      const availableVariables = this.hasAggregations
        ? this.generateVariablesWithAggregations(filteredVariables)
        : filteredVariables

      const cleanLayoutVariables = this.layoutVariables.filter(v => {
        return !(isAdvancedPerformanceView && isPain(v))
      })
      this.selectedVariables = orderBy(cleanLayoutVariables.map(v => ({ ...v })), ['order'])
      this.unselectedVariables = availableVariables.filter(v =>
        !cleanLayoutVariables.find(lv => lv.id === v.id && lv.aggregation === v.aggregation)
      )
    },
    selectVariable(variable) {
      this.unselectedVariables.splice(this.unselectedVariables.indexOf(variable), 1)
      variable.order = this.selectedVariables.length + 1
      this.selectedVariables.push(variable)

      this.updateOrder()
    },
    unselectVariable(variable) {
      this.selectedVariables.splice(this.selectedVariables.indexOf(variable), 1)
      variable.order = null
      this.unselectedVariables.push(variable)

      this.updateOrder()
    },
    updateOrder(event) {
      this.selectedVariables.forEach((variable, index) => {
        variable.order = index + 1
      })

      this.$emit('update')
    },
    generateVariablesWithAggregations(variables) {
      if (!this.showAggregation) return variables

      const variablesWithAggregations = []

      variables.forEach(v => {
        if (v.category !== 'WELLNESS' && v.value !== 'pain' && v.value !== 'reason') {
          this.aggregations.forEach(agg => {
            variablesWithAggregations.push({ ...v, aggregation: agg.value })
          })
        } else {
          variablesWithAggregations.push({ ...v })
        }
      })

      return variablesWithAggregations
    }
  }
}
</script>

<style scoped>

</style>
